import { Navigate, Route, RouteProps, Routes, useSearchParams } from 'react-router-dom';
import PublicLayout from './pages/PublicLayout';
import TeamAccessDenied from './pages/public/TeamAccessDenied';
import PrivateLayout from './pages/PrivateLayout';
import ConfirmEmailChange from './pages/public/ConfirmEmailChange';
import NotFound from './pages/public/NotFound';
import PaymentSuccessful from './pages/public/PaymentSuccessful';
import { lazy, useMemo } from 'react';
import { pathMatch as OldLegacyPaths, redirectHashroutes } from './legacy/components/OldLegacyIframe';
import { pathMatch as LegacyPaths } from './legacy/components/LegacyIframe';
import Subscribe from './pages/private/settings/Subscribe';
import { LegacyPath } from './legacy/components/LegacyIframes';
import PromoCodeTrial from './pages/public/PromoCodeTrial';
import productLift from '@/utils/product-lift';
import { FEATURE_FLAGS, PRODUCT_LIFT_HOST } from '@/constants';
import PageLayoutLoading from './pages/PageLayoutLoading';
import Onboarding from './pages/public/Onboarding';
import posthog from 'posthog-js';
import { POSTHOG_ENABLED } from './providers/posthog-provider';
import { useQuery } from '@apollo/client';
import { AppInitialLoadDocument } from './graphql/generated/app-initial-load.generated';
import InitialLoading from './pages/InitialLoading';
import { apolloIsKnownError } from './utils/apollo-is-known-error';
import DashboardDev from './pages/private/dashboard/DashboardDev';
import { PlanCalculator } from './components/billing/calculator/PlanCalculator';
import { usersPathFilterAccess, isSwonkieDevWorkspace, isFenixExclusive } from './utils/users-path-filter';
import AuthLogin from './pages/public/auth/AuthLogin';
import AuthMagicLink from './pages/public/auth/AuthMagicLink';
import AuthRegister from './pages/public/auth/AuthRegister';
import AuthResetPassword from './pages/public/auth/AuthResetPassword';
import AuthVerifyEmail from './pages/public/auth/AuthVerifyEmail';
import AuthAccountDeleted from './pages/public/auth/AuthAccountDeleted';
import AuthAcceptInvitation from './pages/public/auth/AuthAcceptInvitation';
import AuthConsent from './pages/public/auth/AuthConsent';
import AuthForgotPassword from './pages/public/auth/AuthForgotPassword';
import AuthTwoFactor from './pages/public/auth/AuthTwoFactor';
import AuthTwoFactorSetup from './pages/public/auth/AuthTwoFactorSetup';
import AuthSelectWorkspace from './pages/public/auth/AuthSelectWorkspace';

redirectHashroutes();

const BETTER_AUTH_UI_ENABLED = ['1', 'true', 'yes', 'on'].includes(
    (import.meta.env.VITE_BETTER_AUTH_UI_ENABLED || '').toLowerCase(),
);
/**
 * `/plan-calculator` entry point — parses the URL query into `initialAddOns` (code → value)
 * so the calculator can be deep-linked with pre-set add-ons. Unknown/invalid params are
 * ignored & clamped inside the calculator.
 */
const PlanCalculatorRoute = () => {
    const [searchParams] = useSearchParams();
    // `?plan=<code>` preselects a plan; all other params are treated as add-on codes.
    const { plan, initialAddOns } = useMemo(() => {
        const { plan, ...rest } = Object.fromEntries(searchParams.entries());
        return { plan, initialAddOns: rest };
    }, [searchParams]);
    return (
        <PlanCalculator
            initialPlanCode={plan}
            initialAddOns={initialAddOns}
            onSubmit={(priceRef) => {
                alert(`Price:\n${priceRef}\nURL:\n${import.meta.env.VITE_APP_ROOT}/subscribe/${priceRef}`);
                console.log(
                    `Price:\n${priceRef}\nURL:\n${import.meta.env.VITE_APP_ROOT}/subscribe/${priceRef}`,
                );
            }}
        />
    );
};

const SettingsWorkspaceBilling = lazy(
    () => import('./pages/private/settings/workspace/SettingsWorkspaceBilling'),
);
const SettingsPersonalGeneral = lazy(
    () => import('./pages/private/settings/personal/SettingsPersonalGeneral'),
);
const SettingsPersonalPrivacy = lazy(
    () => import('./pages/private/settings/personal/SettingsPersonalPrivacy'),
);
const SettingsPersonalSecurity = lazy(
    () => import('./pages/private/settings/personal/SettingsPersonalSecurity'),
);
const SettingsPersonalNotifications = lazy(
    () => import('./pages/private/settings/personal/SettingsPersonalNotifications'),
);
const SettingsWorkspaceGeneral = lazy(
    () => import('./pages/private/settings/workspace/SettingsWorkspaceGeneral'),
);
const SettingsWorkspaceSettings = lazy(
    () => import('./pages/private/settings/workspace/SettingsWorkspaceSettings'),
);
const SettingsWorkspaceApi = lazy(() => import('./pages/private/settings/workspace/SettingsWorkspaceApi'));
const SettingsWorkspaceBrands = lazy(
    () => import('./pages/private/settings/workspace/SettingsWorkspaceBrands'),
);
const SubscriptionExpired = lazy(() => import('./pages/private/settings/SubscriptionExpired'));

const DashboardHome = lazy(() => import('./pages/private/dashboard/Dashboard'));

const Dashboards = lazy(() => import('./pages/private/reports/Dashboards'));
const Dashboard = lazy(() => import('./pages/private/reports/Dashboard'));
const EditDashboard = lazy(() => import('./pages/private/reports/EditDashboard'));

const CreateWidget = lazy(() => import('./pages/private/reports/CreateWidget'));
const EditWidget = lazy(() => import('./pages/private/reports/EditWidget'));

const ExportDashboard = lazy(() => import('./pages/public/exports/ExportDashboard'));

const LinkExpired = lazy(() => import('./pages/public/LinkExpired'));

const DashboardPublic = lazy(() => import('./pages/public/DashboardPublic'));

const PostsPublic = lazy(() => import('./pages/public/PostsPublic'));

const PostsPlanning = lazy(() => import('./pages/private/planning/PostsPlanning'));
const PostsView = lazy(() => import('./pages/private/planning/PostsView'));

export type AppRoute = RouteProps & {
    path: string;
    type: 'private' | 'public';
    description?: string;
    isLegacy?: boolean;
    redirectTo?: string;
    elementSwonkieDev?: React.ReactNode;
};

export const appRoutes: Array<AppRoute> = [
    // Private routes
    {
        path: '/main',
        element: <DashboardHome />,
        elementSwonkieDev: <DashboardDev />,
        type: 'private',
    },
    {
        path: '/home',
        element: <Navigate to="/main" replace />,
        type: 'private',
        description: 'Navigate to the main home dashboard.',
    },

    {
        path: '/dashboard',
        element: <Navigate to="/home" replace />,
        type: 'private',
        description: 'Redirects old dashboard route to home.',
    },
    {
        path: '/dashboards',
        element: <Dashboards />,
        type: 'private',
        description: 'Navigate to the dashboards overview page. Also known as reports.',
    },
    {
        path: '/dashboards/new',
        element: <Dashboards />,
        type: 'private',
        description:
            'Create a new dashboard: pick what to analyse, then the accounts, a template, and how to view it.',
    },
    {
        path: '/dashboards/:id',
        element: <Dashboard />,
        type: 'private',
        description: 'Shows details for a specific dashboard. Parameters: id - dashboardId.',
    },
    {
        path: '/dashboards/create/:id?',
        element: <Dashboards />,
        type: 'private',
        description:
            'Create a new dashboard. Parameters: id - optional dashboardId to create from a template dashboard.',
    },
    {
        path: '/dashboards/:id/edit',
        element: <EditDashboard />,
        type: 'private',
        description: 'Edit an existing dashboard. Parameters: id - dashboardId.',
    },
    {
        path: '/dashboards/:dashId/widgets/create/:type/:id?',
        element: <CreateWidget />,
        type: 'private',
        description:
            'Create a new widget for a dashboard. Parameters: dashId - dashboardId, type - widget type, id - optional widgetId to edit an existing widget.',
    },
    {
        path: '/dashboards/:dashId/widgets/:id/edit',
        element: <EditWidget />,
        type: 'private',
        description: 'Edit a widget in a dashboard. Parameters: dashId - dashboardId, id - widgetId.',
    },
    {
        path: '/settings/personal/general',
        element: <SettingsPersonalGeneral />,
        type: 'private',
        description:
            'General personal settings for the user. Here the user can change their personal account name, email, profile picture, interface language, timezone.',
    },
    {
        path: '/settings/personal/privacy',
        element: <SettingsPersonalPrivacy />,
        type: 'private',
        description:
            'Privacy settings for the user. Here the user can change their privacy settings, such as data exporting their account data (GDPR), and delete their account.',
    },
    ...(BETTER_AUTH_UI_ENABLED
        ? [
              {
                  path: '/settings/personal/security',
                  element: <SettingsPersonalSecurity />,
                  type: 'private' as const,
                  description:
                      'Security settings for the user. Here the user can manage their password and two-factor authentication.',
              },
          ]
        : []),
    {
        path: '/settings/personal/notifications',
        element: <SettingsPersonalNotifications />,
        type: 'private',
        description:
            'Personal notification settings. Choose which channels (e.g. email) deliver each notification type, and access the legacy notification settings.',
    },
    {
        path: '/settings/workspace/general',
        element: <SettingsWorkspaceGeneral />,
        type: 'private',
        description: "General workspace settings. Change workspace's name, picture, language and timezone.",
    },
    {
        path: '/settings/workspace/settings',
        element: <SettingsWorkspaceSettings />,
        type: 'private',
        description:
            'Workspace settings. Configure workspace-wide preferences like smart UTMs default values.',
    },
    {
        path: '/settings/workspace/public-api',
        element: <SettingsWorkspaceApi />,
        type: 'private',
        description:
            'Manage public API keys and MCP (Model Context Protocol) integration for the workspace. ' +
            'Navigate here when the user asks about API access, getting or rotating their API key or App ID, or setting up MCP. ' +
            'When a user asks why a copied connection URL contains "YOUR_API_KEY" or "YOUR_APP_ID": these are literal placeholders. ' +
            '"YOUR_APP_ID" must be replaced with the real App ID (X-API-ID header) and "YOUR_API_KEY" with the real API key (X-API-KEY header). ' +
            'Both values are found on this page under Primary key / Secondary key — both work, Secondary enables zero-downtime rotation.',
    },
    {
        path: '/settings/workspace/brands/:brandId?',
        element: <SettingsWorkspaceBrands />,
        type: 'private',
        description:
            'Brand management for brand workspaces. Create, rename, and delete brands, and connect one social profile per network per brand.',
    },
    {
        path: '/settings/workspace/brands/:brandId?',
        element: <SettingsWorkspaceBrands />,
        type: 'private',
        description:
            'Brand management for brand workspaces. Create, rename, and delete brands, and connect one social profile per network per brand.',
    },
    {
        path: '/settings/workspace/billing',
        element: <SettingsWorkspaceBilling />,
        type: 'private',
        description:
            "Billing settings for the workspace. Here the user can manage their workspace's subscription, payment methods, billing options, and billing history.",
    },
    {
        path: '/settings/subscription-expired',
        element: <SubscriptionExpired />,
        type: 'private',
        description: 'Shows when the workspace subscription has expired.',
    },
    {
        path: '/subscribe/:id',
        element: <Subscribe />,
        type: 'private',
        description:
            'Subscribe to a workspace or plan. Parameters: id - optional workspaceId to subscribe to a specific workspace.',
    },
    {
        path: '/posts',
        element: <Navigate to="calendar" replace />,
        type: 'private',
    },
    {
        path: '/posts/:viewId/post?/:postId?',
        element: (
            <PostsPlanning>
                <PostsView />
            </PostsPlanning>
        ),
        type: 'private',
        description:
            "Navigate to /posts/:viewId to open a posts view. Built-in values for viewId are 'calendar' (calendar view aka planning calendar), 'list' (list view aka planning list), and 'kanban' (kanban view). \
            Any other viewId is treated as a custom posts view UUID. \
            The /post? segment is optional. If /post is included without an ID, a new post is created. \
            If a post ID is provided (by replacing the :postId with a valid ID), the corresponding post is opened.",
    },
    {
        path: '/auth/productlift',
        Component: () => {
            productLift.onSSOToken((ssoToken) => {
                window.location.replace(`${PRODUCT_LIFT_HOST}?sso=${ssoToken}`);
            });
            return <PageLayoutLoading />;
        },
        type: 'private',
        description: 'Handles SSO authentication for ProductLift integration.',
    },
    // Legacy private routes
    ...[...OldLegacyPaths, ...LegacyPaths].map<AppRoute>((paths: LegacyPath) => ({
        path: `/${paths.newPath}/*`,
        type: 'private',
        isLegacy: true,
        description: `Legacy Page. ${paths.andieDescription}`,
    })),
    ...[...OldLegacyPaths, ...LegacyPaths]
        .filter((paths) => paths.oldPath !== paths.newPath)
        .map<AppRoute>((paths: LegacyPath) => ({
            path: `/${paths.oldPath}/*`,
            type: 'private',
            isLegacy: true,
            description: `Legacy Page. ${paths.andieDescription}`,
        })),
    // Public routes
    {
        path: '/team-access-denied',
        element: <TeamAccessDenied />,
        type: 'public',
    },
    {
        path: '/confirm-email-change/:id',
        element: <ConfirmEmailChange />,
        type: 'public',
    },
    {
        path: '/payment-successful',
        element: <PaymentSuccessful />,
        type: 'public',
    },
    {
        path: '/settings/billing/promoCode/*',
        element: <PromoCodeTrial />,
        type: 'public',
    },
    {
        path: '/share/dashboard/:id',
        element: <DashboardPublic />,
        type: 'public',
    },
    {
        path: '/share/posts/:id/:postId?',
        element: <PostsPublic />,
        type: 'public',
    },
    {
        path: '/export/dashboard/:id',
        element: <ExportDashboard />,
        type: 'public',
    },
    {
        path: '/expired',
        element: <LinkExpired />,
        type: 'public',
    },
    {
        path: '/onboarding',
        element: <Onboarding />,
        type: 'public',
    },
    {
        path: '/auth/login',
        element: <AuthLogin />,
        type: 'public',
    },
    {
        path: '/auth/register',
        element: <AuthRegister />,
        type: 'public',
    },
    {
        path: '/auth/accept-invitation',
        element: <AuthAcceptInvitation />,
        type: 'public',
    },
    {
        path: '/auth/consent',
        element: <AuthConsent />,
        type: 'public',
        description:
            'OAuth consent page. Allows users to review and approve scopes requested by an OAuth client.',
    },
    {
        path: '/auth/select-workspace',
        element: <AuthSelectWorkspace />,
        type: 'public',
        description:
            'OAuth workspace selection page. Lets users choose which workspace context to authorize.',
    },
    {
        path: '/auth/forgot-password',
        element: <AuthForgotPassword />,
        type: 'public',
        description: 'Forgot password page. Allows users to request a password reset email.',
    },
    {
        path: '/auth/magic-link',
        element: <AuthMagicLink />,
        type: 'public',
        description:
            'Magic-link landing page. Consumes the sign-in token from the email link and completes the session.',
    },
    {
        path: '/auth/reset-password',
        element: <AuthResetPassword />,
        type: 'public',
        description:
            'Reset-password page. Reads the token from the email link and sets a new password.',
    },
    {
        path: '/auth/verify-email',
        element: <AuthVerifyEmail />,
        type: 'public',
        description:
            'Email-verification landing page. Consumes the token from the email link and marks the email verified.',
    },
    {
        path: '/auth/account-deleted',
        element: <AuthAccountDeleted />,
        type: 'public',
        description:
            'Post-deletion confirmation. Better Auth redirects here after the delete-account email link removes the account; confirms deletion and logs out other tabs.',
    },
    {
        path: '/auth/two-factor',
        element: <AuthTwoFactor />,
        type: 'public',
        description: '2FA challenge page. Allows users to verify a TOTP code and continue sign-in.',
    },
    {
        path: '/auth/two-factor-setup',
        element: <AuthTwoFactorSetup />,
        type: 'public',
        description:
            '2FA re-enrollment page. Shown after first login when a legacy 2FA secret is detected, guiding the user through legacy confirmation and new TOTP setup.',
    },
    // Special routes
    {
        path: '/login',
        Component: () => {
            if (BETTER_AUTH_UI_ENABLED) {
                window.location.replace(`/auth/login${window.location.search}`);
            } else {
                window.location.replace(`${import.meta.env.VITE_LEGACY_APP_ROOT}/login`);
            }
            return null;
        },
        type: 'public',
    },
    {
        path: '/register',
        Component: () => {
            if (BETTER_AUTH_UI_ENABLED) {
                window.location.replace(`/auth/register${window.location.search}`);
            } else {
                window.location.replace(`${import.meta.env.VITE_LEGACY_APP_ROOT}/register`);
            }
            return null;
        },
        type: 'public',
    },
    {
        path: '/plan-calculator',
        element: <PlanCalculatorRoute />,
        type: 'private',
    },
];

const publicRoutes = appRoutes
    .filter((r) => r.type === 'public')
    .map((route, idx) => {
        const { path, element, Component, caseSensitive, index } = route;
        const key = path || '' + idx;
        return (
            <Route
                key={key}
                path={path}
                element={element}
                Component={Component}
                caseSensitive={caseSensitive}
                index={index}
            />
        );
    });

const PrivateRoutes = () => {
    const { loading, data, error, refetch } = useQuery(AppInitialLoadDocument);
    if (loading) {
        return <InitialLoading />;
    }
    if (error && !apolloIsKnownError(error)) {
        throw error;
    }
    if (!data) {
        return null;
    }

    const isSwonkieDev = data.currentWorkspace.isSwonkieDev;

    const privateRoutes = appRoutes
        .filter((r) => r.type === 'private')
        .filter((r) => {
            // Special case, use to redirect users to the new home dashboard
            if (r.path === '/home') {
                const iSwonkieDevOrFenixExclusive =
                    isSwonkieDevWorkspace(data.currentWorkspace) || isFenixExclusive(data.currentWorkspace);
                if (iSwonkieDevOrFenixExclusive) {
                    return true;
                } else if (POSTHOG_ENABLED && posthog.isFeatureEnabled(FEATURE_FLAGS.COMPOSE_BETA)) {
                    return true;
                } else {
                    return false;
                }
            }
            return usersPathFilterAccess(r.path, data.currentWorkspace);
        })
        .map((route, idx) => {
            const { path, element, elementSwonkieDev, Component, caseSensitive, index } = route;
            const key = path || '' + idx;

            return (
                <Route
                    key={key}
                    path={path}
                    element={isSwonkieDev && elementSwonkieDev ? elementSwonkieDev : element}
                    Component={Component}
                    caseSensitive={caseSensitive}
                    index={index === false ? false : undefined}
                />
            );
        });

    return (
        <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route element={<PrivateLayout data={data} refetch={refetch} />}>{privateRoutes}</Route>
            <Route path="*" element={<NotFound />} />
        </Routes>
    );
};

export default function AppRoutes() {
    // URL "/settings/billing/promoCode/" - due to legacy compatibility issues
    return (
        <Routes>
            <Route element={<PublicLayout />}>{publicRoutes}</Route>
            <Route element={<PrivateRoutes />} path="*" />
        </Routes>
    );
}
